import { observable, computed, action, autorun } from 'mobx'

import xhr                                       from 'helpers/XHR'
import { setter }                                from 'mobx-decorators'
import { isEmpty, getOr }                        from 'lodash/fp'
import uiStore                                   from 'stores/UiStore'
import userStore                                 from 'stores/UserStore'
import getFormattedTranscript                    from 'helpers/TranscriptParser'
import getError                                  from 'helpers/ErrorParser'

const IN_PROGRESS = 'Transcription in progress...'

export default class Transcript {
  @setter @observable originalTranscript = null

  @setter @observable isError = false
  @setter @observable isVoicemail = false
  @setter @observable isFetchingTranscript = false
  @setter @observable isObfuscated = false
  @setter @observable isObfuscating = false
  @setter @observable isTranscriptHidden =
    userStore.user.transcriptionsHiddenByDefault

  @setter @observable voiceTranscript = false
  @setter @observable callTranscript = null
  @setter @observable transcriptSeparator = 'Speaker'

  @computed get transcript() {
    if (this.isVoicemail) return this.voiceTranscript

    if (isEmpty(this.callTranscript)) return []

    return getFormattedTranscript(
      this.callTranscript,
      this.transcriptSeparator
    )
  }

  @computed get canShow() {
    return (
      !this.isObfuscated &&
      !this.isFetchingTranscript &&
      !isEmpty(this.transcript)
    )
  }

  @computed get isDeleted() {
    return !this.isFetchingTranscript && this.isObfuscated
  }

  @computed get isEmpty() {
    return !this.isFetchingTranscript && isEmpty(this.transcript)
  }

  @computed get hasBeenRequested() {
    if (
      this.callTranscript === IN_PROGRESS ||
      (this.transcript && this.transcript.length)
    ) {
      return true
    }

    return false
  }

  constructor() {
    this.autoErrorNotifier()
  }

  autoErrorNotifier = () => {
    this.autoErrorDisposer = autorun('Watch errors', () => {
      if (this.isError && !this.isError.hideNotification) {
        uiStore.addNotification({
          title:   this.isError.title,
          message: this.isError.message,
          type:    'error'
        })
      }
    })
  }

  // Actions
  @action translateTranscript = (translatedText) => {
    const matches = translatedText.match(/(\w+\s)\d\s*:/) || []
    const isNotEmpty = !isEmpty(matches) && !isEmpty(matches.slice(1))
    const separator = isNotEmpty
      ? matches.slice(1).find(Boolean)
      : null

    this.setTranscriptSeparator(separator)
    this.setCallTranscript(translatedText)
  }

  @action obfuscateTranscript = async(id) => {
    try {
      this.setIsObfuscating(true)
      const {
        data: { obfuscate_transcript }
      } = await xhr.put(
        `/commo/call_log/obfuscate_transcript/${id}`,
        {
          obfuscate_transcript: true
        }
      )

      this.setIsObfuscated(obfuscate_transcript)
    } catch (err) {
      this.setIsError(getError(err))
    } finally {
      this.setIsObfuscating(false)
    }
  }

  @action fetchCallTranscript = async(id) => {
    try {
      this.setIsFetchingTranscript(true)
      this.setIsError(false)

      const params = {
        show_transcript: true,
        only:            [
          'call_transcripts.call_transcript,obfuscate_transcript'
        ].join(',')
      }
      const { data } = await xhr.get(`/commo/call_log/${id}`, {
        params
      })

      this.updateFromJSON(data)
    } catch (err) {
      this.setIsError(getError(err))
    } finally {
      this.setIsFetchingTranscript(false)
    }
  }

  @action updateFromJSON = (transcript) => {
    const ct = getOr(
      '',
      ['call_transcripts', '0', 'call_transcript'],
      transcript
    )

    this.setCallTranscript(ct)
    this.setOriginalTranscript(ct)
    this.setIsObfuscated(transcript.obfuscate_transcript)
  }

  @action hideTranscript = () => {
    this.setIsTranscriptHidden(!this.isTranscriptHidden)
  }

  @action dispose = () => {
    this.autoErrorDisposer && this.autoErrorDisposer()
  }
}
